import { useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import {
    ListingMatch,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
    Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import Skeleton from '@material-ui/lab/Skeleton'
import { RelatedProductsQuery } from 'api/graphQL/queries/product.queries'
import { ProductSearchResponse } from 'api/graphQL/types/product.type'
import clsx from 'clsx'
import {
    SlickArrowLeft,
    SlickArrowRight,
} from 'pages/ProjectsPage/ProjectOverviewPage/components/ProjectTeam/SlickArrow'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import Slider from 'react-slick'
import AutoSizer from 'react-virtualized-auto-sizer'
import { useLocalStorage } from 'shared/utils/localStrorageUtils'
import { getSessionValue } from 'shared/utils/sessionStorageUtils'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { DialogContext } from '../DialogContext'
import { getFirmName } from '../Grid/ProductListingResultGridItem'
import MiniatureListingCard from './MiniatureListingCard'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        boxShadow: 'none',
        position: 'relative',
        width: '100%',
        height: 120,
        borderRadius: 0,
    },
    cardProjectNote: { minWidth: 1910 },
    content: {
        position: 'absolute',
        zIndex: 2,
        bottom: 0,
        width: '100%',
        background:
            'linear-gradient(89.99deg, #FBB41F 0.01%, rgba(0, 0, 0, 0) 99.58%)',
        height: 35,
        display: 'flex',
        alignItems: 'center',
    },
    contentText: {
        fontSize: pxToRem(38),
        color: theme.palette.common.white,
    },
    media: {
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        position: 'absolute',
    },
    header: {
        background: 'linear-gradient(90.06deg, #5F788A 8.33%, #2F4858 83.33%)',
    },
    headerToolbar: {
        minHeight: 60,
    },
    headerText: {
        color: theme.palette.common.white,
    },
    sliderClass: {
        '&.slick-slider': {
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5),
            margin: 'auto',
        },
        '& .slick-arrow': {
            fontSize: '4rem',
            fill: theme.palette.common.white,
        },
        '& .slick-arrow.slick-prev': {
            width: '1em',
            height: '1em',
            left: theme.spacing(1),
        },
        '& .slick-arrow.slick-next': {
            width: '1em',
            height: '1em',
            right: 0,
        },
        '& .slick-initialized .slick-slide': {
            width: '250px important!',
        },
        '& .slick-slide .slick-active': {
            outline: 'none',
            border: 'none',
        },
        '& .slick-list': {
            [theme.breakpoints.up(750)]: {
                width: pxToRem(640),
            },
            [theme.breakpoints.up('sm')]: {
                width: pxToRem(640),
            },
            [theme.breakpoints.up(800)]: {
                width: pxToRem(670),
            },
            [theme.breakpoints.between(900, 1049)]: {
                width: pxToRem(770),
            },
            [theme.breakpoints.between(1050, 1300)]: {
                width: pxToRem(900),
            },
            [theme.breakpoints.up('lg')]: {
                width: pxToRem(1220),
            },
            [theme.breakpoints.between(1290, 1500)]: {
                width: pxToRem(1160),
            },
            [theme.breakpoints.between(1500, 1700)]: {
                width: pxToRem(1200),
            },
            [theme.breakpoints.up('xl')]: {
                width: pxToRem(1380),
            },
            height: pxToRem(165),
        },
    },
    accordion: {
        backgroundColor: '#f2f2f2',
        position: 'static',
        bottom: '0px',
        width: '100%',
        maxWidth: pxToRem(1920),
    },
    expandedAccordion: {
        '&.Mui-expanded': {
            margin: 0,
            backgroundColor: '#444344',
        },
    },
    expandedAccordionSummary: {
        '&.MuiAccordionSummary-root.Mui-expanded': {
            height: pxToRem(20),
            maxHeight: pxToRem(20),
            minHeight: pxToRem(20),
            paddingTop: theme.spacing(0.75),
        },
    },
    accordionDetails: {
        marginRight: theme.spacing(3.8),
        paddingBottom: theme.spacing(0.5),
        paddingTop: theme.spacing(1),
    },
    accordionText: {
        fontSize: pxToRem(14),
        lineHeight: pxToRem(16),
    },
    expandedBannerText: {
        color: theme.palette.common.white,
    },
    showAllText: {
        fontSize: pxToRem(12),
        lineHeight: pxToRem(14),
    },
    expandedIcon: {
        fill: theme.palette.common.white,
    },
    famNameHeader: {
        color: theme.palette.common.white,
        fontSize: pxToRem(12),
        height: pxToRem(36),
        maxHeight: pxToRem(36),
        lineClamp: 2,
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        maxWidth: pxToRem(195),
        textTransform: 'uppercase',
        [theme.breakpoints.down('md')]: {
            maxWidth: pxToRem(150),
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: pxToRem(130),
        },
    },
    divider: {
        width: pxToRem(2),
        backgroundColor: theme.palette.common.white,
        height: pxToRem(150),
        position: 'absolute',
        top: 10,
    },
    container: {
        display: 'flex !important',
    },
    cardGridItem: {
        width: '85%',
        maxWidth: pxToRem(270),
        marginRight: theme.spacing(1.25),
    },
    autoSizer: {
        width: 'auto !important',
        height: 'auto !important',
    },
}))

const sliderSettings = {
    dots: false,
    slidesToShow: 5,
    infinite: true,
    accessibility: true,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,
    responsive: [
        {
            breakpoint: 1200,
            settings: {
                slidesToShow: 5,
            },
        },
        {
            breakpoint: 1100,
            settings: {
                slidesToShow: 4,
            },
        },
        {
            breakpoint: 800,
            settings: {
                slidesToShow: 3,
            },
        },
        {
            breakpoint: 750,
            settings: {
                slidesToShow: 3,
            },
        },
        {
            breakpoint: 375,
            settings: {
                slidesToShow: 1,
            },
        },
    ],
}
const defaultRelatedProductsFacet = [
    { name: 'isActive', values: ['true'] },
    {
        name: 'listingLevels',
        values: [ListingLevel.PremiumProprietary],
    },
]
interface GroupedRelatedProducts {
    familyName: string
    listings: Partial<ListingMatch>[]
}

interface RelatedProductsBannerProps {
    category: string
    setDialogTab: (value: number) => void
    activeDialogTab: number
    updateDialogContainer: (value: boolean) => void
    userId: string
}
export const RelatedProductsBanner: React.FC<RelatedProductsBannerProps> = ({
    category,
    setDialogTab,
    activeDialogTab,
    updateDialogContainer,
    userId,
}) => {

    const classes = useStyles()
    const { projectId } = useParams<{ projectId?: string }>()
    const sliderRef = React.useRef<Slider | null>(null)

    const {
        data: relatedProducts,
        loading: relatedProductsLoading,
        called: isRelatedProductsCalled,
    } = useQuery<ProductSearchResponse>(RelatedProductsQuery, {
        displayName: 'RelatedProductsQuery',
        fetchPolicy: 'no-cache',
        variables: {
            manufacturerProductSearchInput: {
                facets:
                    category !== 'All Categories'
                        ? [
                            ...defaultRelatedProductsFacet,
                            {
                                name: 'categories',
                                values: [category],
                            },
                        ]
                        : [...defaultRelatedProductsFacet],
                productTypeSelections: [],
                orderBy: 'sectionNames,firmName,productType,productName',
                isOnAE: true,
                logEventSource: LogEventSource.AeRelatedProducts,
            },
        },
        onCompleted: (data) => {
            let groupedRelatedProducts: Partial<GroupedRelatedProducts>[] = []
            data?.listingSearch?.matches?.forEach((relatedProduct) => {
                const famName: string = relatedProduct?.sectionName?.substring(
                    relatedProduct?.sectionName?.indexOf('-') + 1
                ) as string
                const famIndex = groupedRelatedProducts?.findIndex(
                    (famListings) => famListings?.familyName === famName
                )
                if (famIndex > -1) {
                    groupedRelatedProducts[famIndex] = {
                        familyName:
                            groupedRelatedProducts[famIndex]?.familyName,
                        listings: [
                            ...groupedRelatedProducts[famIndex]?.listings!,
                            relatedProduct,
                        ],
                    } as GroupedRelatedProducts
                } else {
                    groupedRelatedProducts.push({
                        familyName: famName,
                        listings: [relatedProduct],
                    } as GroupedRelatedProducts)
                }
            })
            setGroupedRelatedProducts(
                groupedRelatedProducts as GroupedRelatedProducts[]
            )
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error retrieving related products',
                    variant: 'error',
                    id: `ProductListingsPage_RelatedProductsError`,
                },
            })
        },
    })
    const [isExpanded, setIsExpanded] = useLocalStorage({
        key: `RelatedProductsBanner-${userId}-${projectId!}`,
        value:
            getSessionValue(`RelatedProductsBanner-${userId}-${projectId!}`) ??
            true,
    })

    const [leftMostIndex, setLeftMostIndex] = useState<number>(0)
    const [groupedRelatedProducts, setGroupedRelatedProducts] = React.useState<
        GroupedRelatedProducts[] | undefined
    >()
    const {
        handleSlideOpen,
        handleListingId,
        handleSubmissionStatus,
        handleTabChange,
        handleMediaIndex,
        handleFirmLogo,
        listingId,
        mediaIndex,
        handleAdvancedFiltersOpen,
    } = React.useContext(DialogContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const handleShowAllButton = React.useCallback(() => {
        setDialogTab(1)
    }, [setDialogTab])

    const handleAccordionState = React.useCallback(() => {
        setIsExpanded(!isExpanded)
        updateDialogContainer(!isExpanded)
    }, [isExpanded])

    const handleGridItemClick = React.useCallback(
        (match?: Partial<ListingMatch>) => {
            handleSlideOpen?.(true)
            handleListingId?.(match?.listingId)
            handleMediaIndex?.(0)
            handleFirmLogo?.(match?.firmLogoUrl)
            handleSubmissionStatus?.(match?.submissionStatus)
            handleAdvancedFiltersOpen?.(false)
        },
        [
            listingId,
            mediaIndex,
            handleSlideOpen,
            handleListingId,
            handleMediaIndex,
            handleFirmLogo,
            handleSubmissionStatus,
            handleTabChange,
        ]
    )

    const handleSlider = React.useCallback(
        (_: number, nextIndex: number) => {
            setLeftMostIndex(nextIndex)
        },
        [setLeftMostIndex]
    )

    const onInitSlider = React.useCallback(() => {
        setTimeout(() => {
            const slides: NodeListOf<Element> = (sliderRef.current as any).innerSlider.list.querySelectorAll(
                '.slick-slide'
            )

            slides.forEach((slide) => {
                const index = parseInt(
                    slide.getAttribute('data-index') || '-1',
                    10
                )
                const isOriginal =
                    index >= 0 &&
                    index <
                    (relatedProducts?.listingSearch?.matches?.length || 0)

                slide
                    .querySelector('[data-testid^="productListingCardItem"]')
                    ?.setAttribute('tabindex', isOriginal ? '0' : '-1')
                slide
                    .querySelector('[data-testid^="productListingCardItem"]')
                    ?.setAttribute('aria-hidden', isOriginal ? 'false' : 'true')
            })
        }, 0)
    }, [relatedProducts])

    React.useEffect(() => {
        if (!relatedProductsLoading && isRelatedProductsCalled) {
            updateDialogContainer(isExpanded)
        }
    }, [relatedProductsLoading, isRelatedProductsCalled])

    return (
        <AutoSizer
            defaultWidth={1920}
            defaultHeight={200}
            className={classes.autoSizer}
        >
            {({ height, width }) => (
                <Accordion
                    data-testid="relatedProductsBanner"
                    className={classes.accordion}
                    classes={{
                        root: classes.expandedAccordion,
                    }}
                    expanded={isExpanded}
                    square={true}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandLessIcon
                                className={clsx(
                                    isExpanded && classes.expandedIcon
                                )}
                                data-testid={'expandBanner'}
                                onClick={handleAccordionState}
                                onKeyDown={onEnterKeyDown(handleAccordionState)}
                                tabIndex={0}
                            />
                        }
                        classes={{ expanded: classes.expandedAccordionSummary }}
                        data-testid="bannerHeader"
                        tabIndex={-1}
                    >
                        <GridContainer spacing={2}>
                            <GridItem>
                                <Typography
                                    component="span"
                                    className={clsx(
                                        classes.accordionText,
                                        isExpanded && classes.expandedBannerText
                                    )}
                                >{`${relatedProducts?.listingSearch?.count ?? 0
                                    } Related Products`}</Typography>
                            </GridItem>
                            {activeDialogTab !== 1 && (
                                <GridItem
                                    onClick={handleShowAllButton}
                                    onKeyDown={onEnterKeyDown(
                                        handleShowAllButton
                                    )}
                                    tabIndex={0}
                                >
                                    <Typography
                                        component="span"
                                        className={clsx(
                                            isExpanded &&
                                            classes.expandedBannerText,
                                            classes.showAllText
                                        )}
                                    >
                                        Show All
                                    </Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </AccordionSummary>
                    <AccordionDetails
                        className={classes.accordionDetails}
                        data-testid="bannerContents"
                    >
                        {relatedProducts?.listingSearch?.matches?.length! >
                            0 ? (
                            <Slider
                                ref={sliderRef}
                                className={clsx(classes.sliderClass)}
                                {...sliderSettings}
                                beforeChange={handleSlider}
                                onInit={onInitSlider}
                                data-testid="bannerSlider"
                            >
                                {relatedProducts?.listingSearch?.matches?.map(
                                    (relatedProduct, index) => {
                                        const listings = groupedRelatedProducts?.find(
                                            (groupedListing) =>
                                                relatedProduct?.sectionName?.substring(
                                                    relatedProduct?.sectionName?.indexOf(
                                                        '-'
                                                    ) + 1
                                                ) === groupedListing?.familyName
                                        )
                                        const familyName = listings?.familyName
                                        const relatedProductIndex = listings?.listings?.findIndex(
                                            (listing) =>
                                                listing?.listingId ===
                                                relatedProduct?.listingId
                                        )

                                        return (
                                            <GridContainer
                                                key={relatedProduct?.listingId}
                                                classes={{
                                                    root: classes.container,
                                                }}
                                            >
                                                <GridItem
                                                    className={
                                                        classes.cardGridItem
                                                    }
                                                >
                                                    {relatedProductIndex ===
                                                        0 ||
                                                        leftMostIndex === index ? (
                                                        <Typography
                                                            className={
                                                                classes.famNameHeader
                                                            }
                                                            data-testid={
                                                                'familyNameHeader'
                                                            }
                                                            component={'div'}
                                                        >
                                                            {familyName}
                                                        </Typography>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                height: 36,
                                                            }}
                                                        />
                                                    )}
                                                    <MiniatureListingCard
                                                        key={
                                                            relatedProduct?.listingId
                                                        }
                                                        firmId={
                                                            relatedProduct?.firmId
                                                        }
                                                        firmName={getFirmName(
                                                            relatedProduct?.displayNameWithFirmName
                                                        )}
                                                        firmLogoUrl={
                                                            relatedProduct?.firmLogoUrl &&
                                                            suffixDimension({
                                                                filename:
                                                                    relatedProduct?.firmLogoUrl,
                                                                dimension: {
                                                                    percentage: 25,
                                                                },
                                                            })
                                                        }
                                                        productName={
                                                            relatedProduct?.productName
                                                        }
                                                        listingId={
                                                            relatedProduct?.listingId
                                                        }
                                                        productType={
                                                            relatedProduct?.productType
                                                        }
                                                        submissionStatus={
                                                            relatedProduct?.submissionStatus
                                                        }
                                                        images={
                                                            relatedProduct?.images
                                                        }
                                                        listingLevel={
                                                            relatedProduct?.type
                                                        }
                                                        catalogs={
                                                            relatedProduct?.catalogs
                                                        }
                                                        bimLinks={
                                                            relatedProduct?.bimLinks
                                                        }
                                                        manufacturerDocs={
                                                            relatedProduct?.manufacturerDocs
                                                        }
                                                        cardAction="Action"
                                                        cardClick={() =>
                                                            handleGridItemClick(
                                                                relatedProduct
                                                            )
                                                        }
                                                    />
                                                </GridItem>
                                                {relatedProductIndex ===
                                                    listings?.listings
                                                        ?.length! -
                                                    1 && (
                                                        <GridItem
                                                            style={{
                                                                width: '15px',
                                                                margin: 'auto',
                                                            }}
                                                        >
                                                            <Divider
                                                                orientation="vertical"
                                                                className={
                                                                    classes.divider
                                                                }
                                                                flexItem
                                                            />
                                                        </GridItem>
                                                    )}
                                            </GridContainer>
                                        )
                                    }
                                )}
                            </Slider>
                        ) : (
                            <Skeleton></Skeleton>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}
        </AutoSizer>
    )
}
